import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, AlertTriangle, FileText } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import FinancialIndicatorsSection from './FinancialIndicatorsSection';
import CompanyProfileSection from './CompanyProfileSection';
import QualitativeFactorsSection from './QualitativeFactorsSection';
import CalculationModelSection from './CalculationModelSection';

// Импорт утилитарных функций
import { isPositiveNumber, isNonNegativeNumber } from '../../../../../utils/validation';
import * as bankruptcyCalculations from '../../../../../utils/bankruptcy';

/**
 * Компонент основной формы расчета риска банкротства
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setAnalysisData,
  companyTypes,
  industryTypes,
  bankruptcyModels,
  saveAssessment 
}) => {
  
  // Состояние для имени сохраняемой оценки
  const [saveAssessmentName, setSaveAssessmentName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeQualitativeFactors' && !checked) {
        newState.creditHistory = 'good';
        newState.pendingLawsuits = false;
        newState.managementChanges = false;
        newState.delayedPayments = false;
        newState.supplierRelationships = 'good';
      }
      
      // Автоматический расчет рабочего капитала
      if (['currentAssets', 'currentLiabilities'].includes(name)) {
        const currentAssets = name === 'currentAssets' ? parseFloatOrDefault(value, 0) : parseFloatOrDefault(prev.currentAssets, 0);
        const currentLiabilities = name === 'currentLiabilities' ? parseFloatOrDefault(value, 0) : parseFloatOrDefault(prev.currentLiabilities, 0);
        newState.workingCapital = (currentAssets - currentLiabilities).toString();
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // Парсинг числовых значений для основных финансовых показателей
    const totalAssets = parseFloatOrDefault(formData.totalAssets, -1);
    const totalLiabilities = parseFloatOrDefault(formData.totalLiabilities, -1);
    const currentAssets = parseFloatOrDefault(formData.currentAssets, -1);
    const currentLiabilities = parseFloatOrDefault(formData.currentLiabilities, -1);
    const retainedEarnings = parseFloatOrDefault(formData.retainedEarnings, -1);
    const ebit = parseFloatOrDefault(formData.ebit, -1);
    const revenue = parseFloatOrDefault(formData.revenue, -1);
    const equity = parseFloatOrDefault(formData.equity, -1);
    const workingCapital = parseFloatOrDefault(formData.workingCapital, -1);
    const companyAge = parseFloatOrDefault(formData.companyAge, -1);

    // Валидация базовых финансовых показателей
    if (!isNonNegativeNumber(totalAssets)) newErrors.totalAssets = 'Сумма активов должна быть неотрицательным числом.';
    if (!isNonNegativeNumber(totalLiabilities)) newErrors.totalLiabilities = 'Сумма обязательств должна быть неотрицательным числом.';
    if (!isNonNegativeNumber(currentAssets)) newErrors.currentAssets = 'Оборотные активы должны быть неотрицательным числом.';
    if (!isNonNegativeNumber(currentLiabilities)) newErrors.currentLiabilities = 'Текущие обязательства должны быть неотрицательным числом.';
    
    // Проверка логической совместимости данных
    if (currentAssets > totalAssets && totalAssets > 0) {
      newErrors.currentAssets = 'Оборотные активы не могут превышать общую сумму активов.';
    }
    
    if (currentLiabilities > totalLiabilities && totalLiabilities > 0) {
      newErrors.currentLiabilities = 'Текущие обязательства не могут превышать общую сумму обязательств.';
    }
    
    // Валидация EBIT и прибыли
    if (ebit < 0 && !newErrors.ebit) {
      // EBIT может быть отрицательным, это не ошибка, но добавим предупреждение
    }
    
    if (retainedEarnings < 0 && !newErrors.retainedEarnings) {
      // Нераспределенная прибыль может быть отрицательной, это не ошибка, но добавим предупреждение
    }
    
    // Валидация выручки
    if (!isNonNegativeNumber(revenue)) newErrors.revenue = 'Выручка должна быть неотрицательным числом.';
    
    // Валидация собственного капитала
    if (!isNonNegativeNumber(equity) && equity !== 0) newErrors.equity = 'Собственный капитал должен быть числом.';
    
    // Валидация типа предприятия и отрасли
    if (!formData.companyType) newErrors.companyType = 'Выберите тип предприятия.';
    if (!formData.industryType) newErrors.industryType = 'Выберите отрасль деятельности.';
    
    // Валидация возраста компании
    if (!isPositiveNumber(companyAge)) newErrors.companyAge = 'Возраст компании должен быть положительным числом.';
    
    // Валидация выбора модели расчета
    if (!formData.calculationModel) newErrors.calculationModel = 'Выберите модель оценки.';
    
    // Проверка качественных факторов (если включены)
    if (formData.includeQualitativeFactors) {
      if (!formData.creditHistory) newErrors.creditHistory = 'Выберите кредитную историю.';
      if (!formData.supplierRelationships) newErrors.supplierRelationships = 'Выберите характеристику отношений с поставщиками.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета риска банкротства
  const calculateBankruptcyRisk = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setAnalysisData(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const financialData = {
          totalAssets: parseFloatOrDefault(formData.totalAssets),
          totalLiabilities: parseFloatOrDefault(formData.totalLiabilities),
          currentAssets: parseFloatOrDefault(formData.currentAssets),
          currentLiabilities: parseFloatOrDefault(formData.currentLiabilities),
          retainedEarnings: parseFloatOrDefault(formData.retainedEarnings),
          ebit: parseFloatOrDefault(formData.ebit),
          revenue: parseFloatOrDefault(formData.revenue),
          equity: parseFloatOrDefault(formData.equity),
          workingCapital: parseFloatOrDefault(formData.workingCapital),
          companyAge: parseFloatOrDefault(formData.companyAge)
        };

        const companyProfile = {
          companyType: formData.companyType,
          industryType: formData.industryType
        };

        // Качественные факторы (если включены)
        const qualitativeFactors = formData.includeQualitativeFactors ? {
          creditHistory: formData.creditHistory,
          pendingLawsuits: formData.pendingLawsuits,
          managementChanges: formData.managementChanges,
          delayedPayments: formData.delayedPayments,
          supplierRelationships: formData.supplierRelationships
        } : null;

        // Выбранная модель оценки
        const calculationModel = formData.calculationModel;

        // Выполняем расчет риска банкротства в зависимости от выбранной модели
        let result, analysisData;

        switch (calculationModel) {
          case 'altman-z':
            result = bankruptcyCalculations.calculateAltmanZScore(financialData, companyProfile);
            analysisData = bankruptcyCalculations.getAltmanZAnalysis(result, financialData);
            break;
          case 'fulmer':
            result = bankruptcyCalculations.calculateFulmerModel(financialData, companyProfile);
            analysisData = bankruptcyCalculations.getFulmerAnalysis(result, financialData);
            break;
          case 'springate':
            result = bankruptcyCalculations.calculateSpringateModel(financialData);
            analysisData = bankruptcyCalculations.getSpringateAnalysis(result, financialData);
            break;
          case 'taffler':
            result = bankruptcyCalculations.calculateTafflerModel(financialData);
            analysisData = bankruptcyCalculations.getTafflerAnalysis(result, financialData);
            break;
          case 'beaver':
            result = bankruptcyCalculations.calculateBeaverModel(financialData);
            analysisData = bankruptcyCalculations.getBeaverAnalysis(result, financialData);
            break;
          case 'saifullin-kadykov':
            result = bankruptcyCalculations.calculateSaifullinKadykovModel(financialData);
            analysisData = bankruptcyCalculations.getSaifullinKadykovAnalysis(result, financialData);
            break;
          case 'comprehensive':
            // Комплексная оценка использует несколько моделей
            const altmanResult = bankruptcyCalculations.calculateAltmanZScore(financialData, companyProfile);
            const fulmerResult = bankruptcyCalculations.calculateFulmerModel(financialData, companyProfile);
            const springateResult = bankruptcyCalculations.calculateSpringateModel(financialData);
            const tafflerResult = bankruptcyCalculations.calculateTafflerModel(financialData);
            
            result = bankruptcyCalculations.calculateComprehensiveAssessment({
              altman: altmanResult,
              fulmer: fulmerResult,
              springate: springateResult,
              taffler: tafflerResult
            }, qualitativeFactors);
            
            analysisData = bankruptcyCalculations.getComprehensiveAnalysis(result, {
              altman: altmanResult,
              fulmer: fulmerResult,
              springate: springateResult,
              taffler: tafflerResult
            }, financialData);
            break;
          default:
            throw new Error(`Неподдерживаемая модель расчета: ${calculationModel}`);
        }

        // Применяем корректировки на основе качественных факторов, если они включены
        if (qualitativeFactors && calculationModel !== 'comprehensive') {
          result = bankruptcyCalculations.adjustResultWithQualitativeFactors(result, qualitativeFactors, calculationModel);
          analysisData = bankruptcyCalculations.adjustAnalysisWithQualitativeFactors(analysisData, qualitativeFactors);
        }

        // Формируем финальные результаты
        const finalResults = {
          score: result.score,
          riskLevel: result.riskLevel,
          riskDescription: result.description,
          interpretation: result.interpretation,
          recommendations: result.recommendations,
          calculationModel: calculationModel,
          includesQualitativeFactors: !!qualitativeFactors,
          companyType: companyProfile.companyType,
          industryType: companyProfile.industryType,
          financialData: { ...financialData },
          // Рассчитанные коэффициенты
          ratios: result.ratios,
          // Пороговые значения для модели
          thresholds: result.thresholds
        };

        // Устанавливаем результаты
        setResults(finalResults);
        setAnalysisData(analysisData);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте другую модель.` });
        setResults(null);
        setAnalysisData(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setAnalysisData, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      totalAssets: '1000000',
      totalLiabilities: '750000',
      currentAssets: '400000',
      currentLiabilities: '300000',
      retainedEarnings: '150000',
      ebit: '120000',
      revenue: '900000',
      equity: '250000',
      workingCapital: '100000',
      
      companyType: 'llc',
      industryType: 'retail',
      companyAge: '5',
      
      includeQualitativeFactors: false,
      creditHistory: 'good',
      pendingLawsuits: false,
      managementChanges: false,
      delayedPayments: false,
      supplierRelationships: 'good',
      
      calculationModel: 'altman-z',
    });
    setResults(null);
    setAnalysisData(null);
    setErrors({});
    setSaveAssessmentName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Создаем заголовки для CSV
    const headers = ['Показатель', 'Значение'];
    
    // Формируем строки данных
    const rows = [
      ['Модель оценки', getBankruptcyModelName(results.calculationModel)],
      ['Итоговый показатель', results.score.toFixed(3)],
      ['Уровень риска', results.riskLevel],
      ['Интерпретация', results.interpretation],
      [''],
      ['ФИНАНСОВЫЕ ПОКАЗАТЕЛИ'],
      ['Общие активы', results.financialData.totalAssets.toLocaleString()],
      ['Общие обязательства', results.financialData.totalLiabilities.toLocaleString()],
      ['Оборотные активы', results.financialData.currentAssets.toLocaleString()],
      ['Текущие обязательства', results.financialData.currentLiabilities.toLocaleString()],
      ['Нераспределенная прибыль', results.financialData.retainedEarnings.toLocaleString()],
      ['EBIT (прибыль до вычета %)', results.financialData.ebit.toLocaleString()],
      ['Выручка', results.financialData.revenue.toLocaleString()],
      ['Собственный капитал', results.financialData.equity.toLocaleString()],
      ['Рабочий капитал', results.financialData.workingCapital.toLocaleString()],
      [''],
      ['РАСЧЁТНЫЕ КОЭФФИЦИЕНТЫ']
    ];
    
    // Добавляем рассчитанные коэффициенты
    if (results.ratios) {
      Object.entries(results.ratios).forEach(([key, value]) => {
        rows.push([key, value.toFixed(3)]);
      });
    }
    
    // Добавляем рекомендации
    if (results.recommendations && results.recommendations.length > 0) {
      rows.push([''], ['РЕКОМЕНДАЦИИ']);
      results.recommendations.forEach((recommendation, index) => {
        rows.push([`Рекомендация ${index + 1}`, recommendation]);
      });
    }
    
    // Преобразуем в CSV формат
    const csvContent = '\uFEFF' + rows.map(row => row.join(';')).join('\n');
    
    // Создаем и скачиваем файл
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `отчет_риска_банкротства_${formatDate(new Date())}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Форматирование даты для имени файла
  const formatDate = (date) => {
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  };
  
  // Получение названия модели по id
  const getBankruptcyModelName = (modelId) => {
    const model = bankruptcyModels.find(m => m.id === modelId);
    return model ? model.name : modelId;
  };

  // Обработчик сохранения результатов
  const handleSaveAssessment = () => {
    if (showSaveDialog) {
      const savedResult = saveAssessment(saveAssessmentName || null);
      if (savedResult) {
        alert(`Результаты оценки "${savedResult.name}" успешно сохранены!`);
      }
      setShowSaveDialog(false);
      setSaveAssessmentName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateBankruptcyRisk();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-orange-500" />
          Оценка риска банкротства
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные финансовые показатели */}
        <FinancialIndicatorsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Профиль компании */}
        <CompanyProfileSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          companyTypes={companyTypes}
          industryTypes={industryTypes}
        />

        {/* Качественные факторы */}
        <QualitativeFactorsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Выбор модели расчета */}
        <CalculationModelSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          bankruptcyModels={bankruptcyModels}
        />

        {/* Диалог сохранения результатов */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveAssessmentName}
                onChange={(e) => setSaveAssessmentName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveAssessment}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<AlertTriangle className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Оценить риск банкротства'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать отчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveAssessment}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить результаты
            </Button>
          )}
          {results && (
            <Button
              type="button"
              onClick={() => window.print()}
              icon={<FileText className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Распечатать отчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;